import { auth } from '../lib/firebase';
import { normalizeBackendUrl } from './backendUrl';

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL);

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Vous devez être connecté en admin.');
  return user.getIdToken();
}

async function apiFetch<T>(
  path: string,
  init: { method?: 'GET' | 'POST' | 'PUT'; body?: Record<string, unknown> } = {}
): Promise<T> {
  const token = await getIdToken();
  const method = init.method || 'GET';
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...(init.body ? { body: JSON.stringify(init.body) } : {}),
  });

  let payload: Record<string, unknown> = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const message =
      (payload?.error as string) ||
      (payload?.message as string) ||
      `Request failed (${response.status})`;
    const err = new Error(message) as Error & {
      status?: number;
      code?: string;
      activeRunId?: string;
    };
    err.status = response.status;
    err.code = payload?.code as string | undefined;
    err.activeRunId = payload?.activeRunId as string | undefined;
    throw err;
  }

  return payload as T;
}

export interface DataSyncPublicConfig {
  configured: boolean;
  hasPrivateKey: boolean;
  projectId: string | null;
  clientEmail: string | null;
  storageBucket: string | null;
  updatedAt?: unknown;
  updatedBy?: string | null;
}

export interface DataSyncRun {
  id: string;
  type: 'organizations' | 'recipes' | 'ingredients';
  status: 'pending' | 'running' | 'completed' | 'failed';
  total: number;
  processed: number;
  copied: number;
  overwritten: number;
  failed: number;
  errors: Array<{ id?: string; collection?: string; error: string }>;
  currentLabel: string;
  options?: Record<string, unknown>;
  startedAt?: unknown;
  finishedAt?: unknown;
}

export type SyncIds = string[] | 'all';

export interface OrgSyncOptions {
  includeRecipes?: boolean;
  includeCookbook?: boolean;
  includeLinkedIngredients?: boolean;
}

export interface RecipeSyncOptions {
  includeLinkedIngredients?: boolean;
}

export function getDataSyncConfig(): Promise<DataSyncPublicConfig> {
  return apiFetch<DataSyncPublicConfig>('/api/data-sync/config');
}

export function saveDataSyncConfig(body: {
  projectId: string;
  clientEmail?: string;
  privateKey?: string;
  storageBucket?: string;
}): Promise<DataSyncPublicConfig> {
  return apiFetch<DataSyncPublicConfig>('/api/data-sync/config', {
    method: 'PUT',
    body,
  });
}

export function testDataSyncConfig(): Promise<{
  ok: boolean;
  projectId?: string | null;
  collectionsCount?: number;
  error?: string;
}> {
  return apiFetch('/api/data-sync/config/test', { method: 'POST' });
}

export function createDataSyncRun(params: {
  type: 'organizations' | 'recipes' | 'ingredients';
  ids: SyncIds;
  options?: OrgSyncOptions | RecipeSyncOptions;
}): Promise<{ runId: string }> {
  return apiFetch<{ runId: string }>('/api/data-sync/runs', {
    method: 'POST',
    body: params as Record<string, unknown>,
  });
}

export function getDataSyncRun(runId: string): Promise<DataSyncRun> {
  return apiFetch<DataSyncRun>(`/api/data-sync/runs/${encodeURIComponent(runId)}`);
}

export function getActiveDataSyncRun(): Promise<DataSyncRun | null> {
  return apiFetch<DataSyncRun | null>('/api/data-sync/runs/active');
}
